import { useEffect, useState } from 'react'

const REDUCE = '(prefers-reduced-motion: reduce)'

/**
 * Whether to skip motion right now.
 *
 * True when the reader has asked for less motion, and also when the
 * environment cannot answer the question at all — jsdom has no `matchMedia`,
 * and a test asserting on a figure must see the figure, not a frame of its
 * count-up. Every motion the console runs from script asks this first; motion
 * in CSS is switched off by the stylesheet's own reduced-motion rules.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  return window.matchMedia(REDUCE).matches
}

/** `prefersReducedMotion`, kept current when the reader changes the setting. */
export function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(prefersReducedMotion)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined
    const query = window.matchMedia(REDUCE)
    const update = () => setReduce(query.matches)
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  return reduce
}

/** True for a device with a precise pointer that can hover — where light that follows it makes sense. */
export function hasFinePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches
}
